/* eslint-disable react-refresh/only-export-components -- utility module with one component at the end */
import { logException, logEvent } from "@classdojo/log-client";
import React from "react";
import type { JSONObject } from "./types";

export const MEANINGFUL_TAG_NAMES = new Set<string>(["A", "BUTTON", "SELECT", "DETAILS"]);
export const MEANINGFUL_ATTR_VALUES = {
  role: new Set<string>(["button", "checkbox", "link", "switch"]),
  type: new Set<string>(["checkbox"]),
};
export const LESS_MEANINGFUL_TAG_NAME_WITH_DATA_NAME = new Set<string>(["LABEL"]);

export function _shouldLog(node: Element): boolean {
  if (node.attributes.getNamedItem("aria-disabled")?.value === "true") {
    return false;
  }

  for (const [attr, values] of Object.entries(MEANINGFUL_ATTR_VALUES)) {
    const attrValue = node.attributes.getNamedItem(attr)?.value;
    if (attrValue && values.has(attrValue)) {
      return true;
    }
  }

  if (node.attributes.getNamedItem("data-name") && LESS_MEANINGFUL_TAG_NAME_WITH_DATA_NAME.has(node.tagName)) {
    return true;
  }

  return MEANINGFUL_TAG_NAMES.has(node.tagName);
}

const mongoIdRegex = /[0-9a-fA-F]{24}/g;
const replaceMongoId = (str: string): string => str.replace(mongoIdRegex, "{id}");

const INSTALL_REFERRER_PARAM = "referrer";
export const INSTALL_REFERRER_DEEPLINK_PARAM = "dojo_link";

const scrubDeeplink = (deeplink: string): string => {
  try {
    const { origin, pathname } = new URL(deeplink);
    return `${origin}${pathname}`;
  } catch (error) {
    if (error instanceof TypeError) {
      return "{deeplink}";
    }
    throw error;
  }
};

export function _scrubInstallReferrerDeeplink(href: string): string {
  if (!href.includes(INSTALL_REFERRER_DEEPLINK_PARAM)) {
    return href;
  }

  try {
    const url = new URL(href, "https://relative.invalid");
    const referrer = url.searchParams.get(INSTALL_REFERRER_PARAM);
    if (!referrer) {
      return href;
    }

    const referrerParams = new URLSearchParams(referrer);
    const deeplink = referrerParams.get(INSTALL_REFERRER_DEEPLINK_PARAM);
    if (!deeplink) {
      return href;
    }

    referrerParams.set(INSTALL_REFERRER_DEEPLINK_PARAM, scrubDeeplink(deeplink));
    url.searchParams.set(INSTALL_REFERRER_PARAM, referrerParams.toString());
    return url.origin === "https://relative.invalid" ? `${url.pathname}${url.search}${url.hash}` : url.toString();
  } catch (error) {
    if (error instanceof TypeError) {
      return href;
    }
    throw error;
  }
}

function _getStandardNodeMetadata(node: Element): JSONObject {
  const metadata: JSONObject = {};

  const ariaToggleState = node.getAttribute("aria-checked") ?? node.getAttribute("aria-pressed");

  if (ariaToggleState) {
    metadata.toggleTransition = {
      // These aria attributes are updated in the React render cycle, *after* the click event
      // is processed, so they reflect the previous ("from") state.
      from: ariaToggleState === "true" ? "on" : "off",
      to: ariaToggleState === "true" ? "off" : "on",
    };
  }

  if (node instanceof HTMLInputElement && node.type === "checkbox") {
    // The native HTML checkbox "checked" attribute is updated immediately before the
    // click event is dispatched, so it reflects the new ("to") state.
    metadata.toggleTransition = {
      from: node.checked ? "off" : "on",
      to: node.checked ? "on" : "off",
    };
  }

  return metadata;
}

export function _findNodeToLogAndAncestorTrace(node: Element | null): {
  nodeToLog: Element | null;
  ancestorTrace: string[];
  metadata: JSONObject;
  anonymizeUser: boolean;
} {
  if (!node) return { nodeToLog: null, ancestorTrace: [], metadata: {}, anonymizeUser: false };

  let nodeToLog: Element | null = null;
  const ancestorTrace: string[] = [];
  let metadata = {};

  let anonymizeUser = false;

  // Climb the DOM tree
  for (let i = 0; node && node !== document.body && i < 100; i++) {
    // Find the closest meaningful node to log
    if (!nodeToLog && _shouldLog(node)) {
      nodeToLog = node;
      metadata = { ...metadata, ..._getStandardNodeMetadata(nodeToLog) };
    } else if (nodeToLog) {
      // Add relevant ancestors, with data-name, to a stack trace for logs
      const name = node.getAttribute("data-name");
      if (name) {
        ancestorTrace.push(name);
      }
    }

    const nodeMetadata = node.getAttribute("data-event-metadata");
    if (nodeMetadata) {
      try {
        const parsed = JSON.parse(nodeMetadata) as JSONObject;
        // prefer existing metadata to ancestor metadata
        metadata = { ...parsed, ...metadata };
        // eslint-disable-next-line no-catch-all/no-catch-all
      } catch (x) {
        logException(x instanceof Error ? x : new Error(String(x)), ["error collecting node metadata"]);
      }
    }

    anonymizeUser ||= node.getAttribute("data-event-anonymize-user") === "true";

    node = (node as Element).parentElement;
  }
  return { nodeToLog, ancestorTrace, metadata, anonymizeUser };
}

export function _isElement(obj: null | EventTarget | Element): obj is Element {
  return (obj as Element)?.tagName !== undefined;
}

export interface LogClickOptions {
  anchorHref?: "include" | "omit";
}

function shouldLogNamedNodeOrAnchor(name: string | null, isAnchor: boolean, anchorHref: "include" | "omit") {
  return Boolean(name) || (isAnchor && anchorHref === "include");
}

function getAnchorMetadata(metadata: JSONObject, node: Element, anchorHref: "include" | "omit"): JSONObject {
  if (anchorHref === "omit") {
    const sanitizedMetadata = { ...metadata };
    delete sanitizedMetadata.href;
    return sanitizedMetadata;
  }

  const href = node.getAttribute("href");
  return { ...metadata, href: href ? replaceMongoId(_scrubInstallReferrerDeeplink(href)) : null };
}

/**
 * As part of the Automatic Events project, creates a logEvent for clicks within a particular
 * app. https://linear.app/classdojo/issue/DSOL-3316/automatic-events
 *
 * To configure, call this function as part of a "click" EventListener.
 * Example:
 *    document.body.addEventListener("click", (ev) => logClick(ev, "teach"), { capture: true });
 *
 * Apps that must not record anchor destinations can pass `{ anchorHref: "omit" }`. In that mode,
 * named anchors log without href metadata and unnamed anchors are skipped.
 */
export function logClick(ev: MouseEvent, site: string, { anchorHref = "include" }: LogClickOptions = {}): void {
  try {
    if (!ev || !_isElement(ev.target)) return;

    const node: Element = ev.target;
    const { nodeToLog, ancestorTrace, metadata, anonymizeUser } = _findNodeToLogAndAncestorTrace(node);

    if (!nodeToLog) return;
    const name = nodeToLog.getAttribute("data-name");
    const experiments = nodeToLog.getAttribute("data-experiments")?.split(",");
    const tagName = nodeToLog.tagName.toLowerCase();
    const isAnchor = tagName === "a";
    if (!shouldLogNamedNodeOrAnchor(name, isAnchor, anchorHref)) return;

    // Passing `null` will overwrite any default values for entityId and userType
    const userTracking = anonymizeUser
      ? {
          entityId: null,
          userType: null,
          sessionId: "unknown",
        }
      : {};

    if (isAnchor) {
      logEvent({
        ...userTracking,

        eventName: name ? `${site}.${tagName}.${name}.click` : `${site}.${tagName}.click`,
        automatedEvent: true,
        metadata: {
          ...getAnchorMetadata(metadata, nodeToLog, anchorHref),
          site,
          ancestorTrace,
          immediateAncestor: ancestorTrace.length > 0 ? ancestorTrace[0] : null,
        },
        experiments,
      });
      return;
    }

    logEvent({
      ...userTracking,

      eventName: `${site}.${tagName}.${name}.click`,
      automatedEvent: true,
      metadata: {
        ...metadata,
        site,
        ancestorTrace,
        immediateAncestor: ancestorTrace.length > 0 ? ancestorTrace[0] : null,
      },
      experiments,
    });
    // eslint-disable-next-line no-catch-all/no-catch-all
  } catch (err) {
    logException(err instanceof Error ? err : new Error(String(err)), {
      message: "Error in logClick global event handler",
    });
  }
}

export function EventAnonymizeUser({ children, className }: React.PropsWithChildren & { className?: string }) {
  return (
    <div className={className} data-event-anonymize-user="true">
      {children}
    </div>
  );
}
